import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { getCreditNotes } from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import { useSort } from '../hooks/useSort'
import SortHeader from '../components/ui/SortHeader'
import Pagination from '../components/ui/Pagination'
import StatusBadge from '../components/ui/StatusBadge'
import Avatar from '../components/ui/Avatar'
import EmptyState from '../components/ui/EmptyState'

export default function CreditNotes() {
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getCreditNotes({ take: 9999 })
      .then((res) => setNotes(res.data.creditNotes || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const { paged, page, setPage, totalPages, total, sortKey, sortDir, toggleSort, pageSize } = useSort(notes, {
    defaultSort: 'date', defaultDir: 'desc', pageSize: 25, storageKey: 'credit-notes',
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Credit Notes</h1>
        <Link to="/credit-notes/new" className="btn-primary flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> New Credit Note
        </Link>
      </div>

      <div className="card overflow-hidden !p-0">
        {loading ? (
          <div className="space-y-3 p-6">{[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : notes.length === 0 ? (
          <EmptyState type="credit-notes" message="No credit notes yet." actionLabel="Create credit note" actionTo="/credit-notes/new" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50/80 border-b">
                    <SortHeader label="Number" sortKey="number" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Customer" sortKey="customer.name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Amount" sortKey="total" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                    <SortHeader label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((cn) => (
                    <tr key={cn.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/credit-notes/${cn.id}`} className="text-brand-600 hover:underline font-medium">
                          {cn.number || 'DRAFT'}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar name={cn.customer?.name} />
                          <span className="text-gray-600">{cn.customer?.name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(cn.date)}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(cn.total, cn.currency)}</td>
                      <td className="px-4 py-3"><StatusBadge status={cn.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  )
}
